import { useTranslation } from 'react-i18next';

import type { TeamCategory } from '@/api/teamCategories';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CategoryBasicsTab } from './CategoryBasicsTab';
import { CategoryRulesTab } from './CategoryRulesTab';
import { CategoryFeesTab } from './CategoryFeesTab';
import { CategoryNoticesTab } from './CategoryNoticesTab';

// Unified team-category editor: one right-side drawer with tabs, mirroring
// the member-detail drawer (Sheet + Tabs). Replaces the two separate
// dialogs (basics + scheduling rules). The active tab is URL-driven
// (?tab=…) so the AI navigate tool can deep-link "open Ungdom's schedule
// rules" straight to the rules tab. A future Aviseringar (notices-config)
// tab slots in here alongside the other two.

export type CategoryTab = 'category' | 'rules' | 'fees' | 'notices';

export interface TeamCategorySheetProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  // Undefined in create mode — only the "Kategori" tab shows until the
  // category exists (rules need its id).
  category?: TeamCategory | undefined;
  tab: CategoryTab;
  onTabChange: (tab: CategoryTab) => void;
  // Owner/administrator. The drawer is normally only reachable from a row
  // action they alone see, but it's also URL-driven (?edit=…), so the club-wide
  // membership editor on the fees tab gates on this explicitly.
  canManage: boolean;
  // Called with the freshly-created category so the parent can flip the URL
  // to ?edit=<id> and reveal the remaining tabs.
  onCreated: (saved: TeamCategory) => void;
}

export function TeamCategorySheet({
  open,
  onClose,
  slug,
  category,
  tab,
  onTabChange,
  canManage,
  onCreated,
}: TeamCategorySheetProps) {
  const { t } = useTranslation('common');

  const title = category
    ? t('settings.teamCategories.modal.editTitle')
    : t('settings.teamCategories.modal.createTitle');

  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent side="right" className="flex h-dvh w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        {/* ONE Tabs root wrapping both the list (in the header) and the panels
            — Radix needs them under the same root, and it's the flex column
            that fills the sheet height so each panel's own scroll + footer land
            correctly. */}
        <Tabs
          value={tab}
          onValueChange={(v) => onTabChange(v as CategoryTab)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <header className="flex shrink-0 flex-col gap-3 border-b border-border p-4 pr-14 sm:p-6 sm:pr-14 sm:pb-4">
            <SheetTitle>{category ? category.name : title}</SheetTitle>
            {category && (
              <TabsList className="self-start">
                <TabsTrigger value="category">
                  {t('settings.teamCategories.tabs.category')}
                </TabsTrigger>
                <TabsTrigger value="rules">
                  {t('settings.teamCategories.tabs.rules')}
                </TabsTrigger>
                <TabsTrigger value="fees">
                  {t('settings.teamCategories.tabs.fees')}
                </TabsTrigger>
                <TabsTrigger value="notices">
                  {t('settings.teamCategories.tabs.notices')}
                </TabsTrigger>
              </TabsList>
            )}
          </header>

          {/* Create mode: basics only (rules/notices need a saved id). */}
          {!category ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <CategoryBasicsTab slug={slug} open={open} onClose={onClose} onSaved={onCreated} />
            </div>
          ) : (
            <>
              {/* TabsContent panels must NOT carry `flex` directly: Radix keeps
                  inactive panels mounted with the `hidden` attribute, and a
                  `flex` utility overrides `[hidden]{display:none}` — which made
                  all three panels render and split the height into thirds. So the
                  panel is a plain block flex-item (`min-h-0 flex-1`) that stays
                  hidden when inactive, and an inner wrapper does the flex fill. */}
              <TabsContent value="category" className="min-h-0 flex-1">
                <div className="flex h-full min-h-0 flex-col">
                  <CategoryBasicsTab
                    slug={slug}
                    category={category}
                    open={open && tab === 'category'}
                    onClose={onClose}
                  />
                </div>
              </TabsContent>
              <TabsContent value="rules" className="min-h-0 flex-1">
                <div className="flex h-full min-h-0 flex-col">
                  <CategoryRulesTab
                    slug={slug}
                    category={category}
                    open={open && tab === 'rules'}
                    onClose={onClose}
                  />
                </div>
              </TabsContent>
              <TabsContent value="fees" className="min-h-0 flex-1">
                <div className="flex h-full min-h-0 flex-col">
                  <CategoryFeesTab
                    slug={slug}
                    category={category}
                    open={open && tab === 'fees'}
                    onClose={onClose}
                    canManage={canManage}
                  />
                </div>
              </TabsContent>
              <TabsContent value="notices" className="min-h-0 flex-1">
                <div className="flex h-full min-h-0 flex-col">
                  <CategoryNoticesTab
                    slug={slug}
                    category={category}
                    open={open && tab === 'notices'}
                    onClose={onClose}
                  />
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
